/** Alineado con el criterio visual de Sitios (ON / OFF / Con novedad / Sin dato). */

export type InventoryCameraOperational = 'on' | 'off' | 'issue' | 'unknown';

export type SiteCamerasOperationalStatus = 'online' | 'offline' | 'partial' | 'unknown';

export type InventoryCameraDeviceOperational = {
  connectivityOnline?: boolean | null;
  lastConnectivityStatus?: boolean | null;
  probeState?: number | null;
  lastHeartbeatAt?: string | null;
};

export function inventoryCameraOperational(
  cam: {
    deviceCamera?: InventoryCameraDeviceOperational | null;
  },
  options?: { useProbeFallback?: boolean },
): InventoryCameraOperational {
  const dc = cam.deviceCamera;
  if (!dc) return 'unknown';
  const cur = dc.connectivityOnline;
  const last = dc.lastConnectivityStatus;
  if (last != null && cur != null && Boolean(last) !== Boolean(cur)) {
    return 'issue';
  }
  if (cur === true) return 'on';
  if (cur === false) return 'off';
  if (options?.useProbeFallback !== false) {
    const probe = dc.probeState;
    if (probe === 1) return 'on';
    if (probe === 0) return 'off';
  }
  return 'unknown';
}

/** ``sites.state_id`` → semáforo (1=ON, 2=novedad, 3=mantenimiento, 4=OFF). */
export function siteOperationalStatusFromStateId(
  stateId?: number | null,
): SiteCamerasOperationalStatus | null {
  const n = stateId != null ? Number(stateId) : NaN;
  if (!Number.isFinite(n)) return null;
  if (n === 1) return 'online';
  if (n === 2 || n === 3) return 'partial';
  if (n === 4) return 'offline';
  return null;
}

/** Estado agregado de un sitio según sus cámaras (fallback si no hay ``state_id``). */
export function siteCamerasOperationalStatus(
  cameras: Array<{ deviceCamera?: InventoryCameraDeviceOperational | null }>,
  options?: { useProbeFallback?: boolean },
): SiteCamerasOperationalStatus {
  if (cameras.length === 0) return 'unknown';

  const ops = cameras.map((c) => inventoryCameraOperational(c, options));
  if (ops.some((o) => o === 'issue')) return 'partial';

  const known = ops.filter((o) => o !== 'unknown');
  if (known.length === 0) return 'unknown';

  const onCount = known.filter((o) => o === 'on').length;
  if (onCount === known.length) return 'online';
  if (onCount === 0) return 'offline';
  return 'partial';
}

const STREAM_STATUS_OPTS = { useProbeFallback: false } as const;

/** 3 ciclos del worker (~300s) sin heartbeat ⇒ sonda obsoleta ⇒ no forzar 'off'. */
const PROBE_STALE_MS = 15 * 60 * 1000;

function isProbeFresh(lastHeartbeatAt?: string | null): boolean {
  if (!lastHeartbeatAt) return false;
  const t = Date.parse(lastHeartbeatAt);
  if (!Number.isFinite(t)) return false;
  return Date.now() - t <= PROBE_STALE_MS;
}

/** Misma regla que ``resolveCameraOperational`` en backend (``GET /cameras``). */
export function resolveCameraOperational(input: {
  connectivityOnline?: boolean | null;
  lastConnectivityStatus?: boolean | null;
  probeState?: number | null;
  lastHeartbeatAt?: string | null;
}): InventoryCameraOperational {
  const probe = input.probeState;
  if (probe === 1) return 'on';
  if (probe === 0 && isProbeFresh(input.lastHeartbeatAt)) return 'off';
  if (probe === 0) return 'unknown';

  const cur = input.connectivityOnline;
  const last = input.lastConnectivityStatus;
  if (last != null && cur != null && Boolean(last) !== Boolean(cur)) {
    return 'issue';
  }
  if (cur === true) return 'on';
  if (cur === false) return 'unknown';
  return 'unknown';
}

/** Lee ``operational`` de ``GET /cameras``; fallback a ``deviceCamera`` si falta (respuestas cacheadas). */
export function streamCameraOperationalState(
  cam: {
    operational?: InventoryCameraOperational | null;
    deviceCamera?: InventoryCameraDeviceOperational | null;
  },
): InventoryCameraOperational {
  if (cam.operational != null) return cam.operational;
  const dc = cam.deviceCamera;
  if (!dc) return 'unknown';
  return resolveCameraOperational(dc);
}

/** Contador online/total a partir de ``operational`` de ``GET /cameras``. */
export function siteCamerasDeviceOnline(
  cameras: Array<{
    operational?: InventoryCameraOperational | null;
    deviceCamera?: InventoryCameraDeviceOperational | null;
  }>,
): { online: number; total: number; hasData: boolean } {
  const states = cameras.map((c) => streamCameraOperationalState(c));
  return {
    online: states.filter((s) => s === 'on').length,
    total: cameras.length,
    hasData: states.some((s) => s !== 'unknown'),
  };
}

/** Semáforo del sitio agregando ``operational`` de cada cámara (``GET /cameras``). */
export function siteStatusFromStreamOperational(
  cameras: Array<{
    operational?: InventoryCameraOperational | null;
    deviceCamera?: InventoryCameraDeviceOperational | null;
  }>,
): SiteCamerasOperationalStatus {
  if (cameras.length === 0) return 'unknown';
  const states = cameras.map((c) => streamCameraOperationalState(c));
  const known = states.filter((s) => s !== 'unknown');
  if (known.length === 0) return 'unknown';
  if (states.some((s) => s === 'issue')) return 'partial';
  const onCount = known.filter((s) => s === 'on').length;
  if (onCount === known.length) return 'online';
  if (onCount === 0) return 'offline';
  return 'partial';
}

/** Color del borde lateral / contador según semáforo del sitio. */
export function siteOperationalModColor(mod: SiteCamerasOperationalStatus): string {
  switch (mod) {
    case 'online':
      return '#10B981';
    case 'offline':
      return '#EF4444';
    case 'partial':
      return '#F59E0B';
    default:
      return '#6b7280';
  }
}

export type SiteOperationalUi = {
  mod: SiteCamerasOperationalStatus;
  label: string;
  title: string;
  online: number;
  total: number;
  hasData: boolean;
  countColor: string;
};

/** Semáforo del sitio en Ver Stream: agrega ``operational`` de ``GET /cameras``. */
export function siteOperationalUi(
  cameras: Array<{
    operational?: InventoryCameraOperational | null;
    deviceCamera?: InventoryCameraDeviceOperational | null;
  }>,
): SiteOperationalUi {
  const counts = siteCamerasDeviceOnline(cameras);
  const mod = siteStatusFromStreamOperational(cameras);
  const countColor = siteOperationalModColor(mod);
  switch (mod) {
    case 'online':
      return { mod, label: 'Operativo', title: 'Todas las cámaras operativas', ...counts, countColor };
    case 'offline':
      return { mod, label: 'Fuera de servicio', title: 'Todas las cámaras fuera de servicio', ...counts, countColor };
    case 'partial':
      return { mod, label: 'Novedad', title: 'Hay cámaras fuera de servicio o con novedad', ...counts, countColor };
    default:
      return { mod, label: 'Sin dato', title: 'Estado de cámaras no disponible', ...counts, countColor };
  }
}

export function streamCameraOperationalUi(cam: {
  operational?: InventoryCameraOperational | null;
  deviceCamera?: InventoryCameraDeviceOperational | null;
}): { mod: InventoryCameraOperational; label: string; title: string } {
  const op = streamCameraOperationalState(cam);
  const hb = cam.deviceCamera?.lastHeartbeatAt
    ? `Último heartbeat: ${new Date(cam.deviceCamera.lastHeartbeatAt).toLocaleString()}`
    : '';
  switch (op) {
    case 'on':
      return {
        mod: 'on',
        label: 'Operativo',
        title: [hb, 'Cámara en línea'].filter(Boolean).join(' — '),
      };
    case 'off':
      return {
        mod: 'off',
        label: 'Fuera de servicio',
        title: [hb, 'Sin conectividad'].filter(Boolean).join(' — '),
      };
    case 'issue':
      return {
        mod: 'issue',
        label: 'Novedad',
        title: [hb, 'Cambio reciente de estado'].filter(Boolean).join(' — '),
      };
    default:
      return {
        mod: 'unknown',
        label: 'Sin dato',
        title: 'Estado no disponible',
      };
  }
}

export function siteCamerasOperationalUi(
  cameras: Array<{ deviceCamera?: InventoryCameraDeviceOperational | null }>,
): { mod: SiteCamerasOperationalStatus; label: string; title: string } {
  const mod = siteCamerasOperationalStatus(cameras, STREAM_STATUS_OPTS);
  switch (mod) {
    case 'online':
      return { mod, label: 'Operativo', title: 'Todas las cámaras operativas' };
    case 'offline':
      return { mod, label: 'Fuera de servicio', title: 'Todas las cámaras fuera de servicio' };
    case 'partial':
      return { mod, label: 'Novedad', title: 'Hay cámaras fuera de servicio o con novedad' };
    default:
      return { mod, label: 'Sin dato', title: 'Estado de cámaras no disponible' };
  }
}

export function cameraOperationalUi(
  cam: {
    deviceCamera?: InventoryCameraDeviceOperational | null;
  },
  options?: { useProbeFallback?: boolean },
): { mod: InventoryCameraOperational; label: string; title: string } {
  const op = inventoryCameraOperational(cam, options);
  const hb = cam.deviceCamera?.lastHeartbeatAt
    ? `Último heartbeat: ${new Date(cam.deviceCamera.lastHeartbeatAt).toLocaleString()}`
    : '';
  switch (op) {
    case 'on':
      return {
        mod: 'on',
        label: 'Operativo',
        title: [hb, 'Cámara en línea'].filter(Boolean).join(' — '),
      };
    case 'off':
      return {
        mod: 'off',
        label: 'Fuera de servicio',
        title: [hb, 'Sin conectividad'].filter(Boolean).join(' — '),
      };
    case 'issue':
      return {
        mod: 'issue',
        label: 'Novedad',
        title: [hb, 'Cambio reciente de estado'].filter(Boolean).join(' — '),
      };
    default:
      return {
        mod: 'unknown',
        label: 'Sin dato',
        title: 'Estado no disponible',
      };
  }
}
